/** @jsx jsx */
import { jsx } from "theme-ui"
import React from "react"
import { Link } from "gatsby"
import {
  MdChevronRight as ChevronRight,
  MdChevronLeft as ChevronLeft,
} from "react-icons/md"
import { getItemList } from "../../utils/sidebar/item-list"
import getActiveItem from "../../utils/sidebar/get-active-item"
import getActiveItemParents from "../../utils/sidebar/get-active-item-parents"
import allDocs from "../sidebar/all-docs-sidebar.yaml"

const Separator = ({ character = <ChevronRight /> }) => (
  <span
    sx={{ my: 0, mx: 2, verticalAlign: `middle`, color: "grey.40" }}
    role="presentation"
  >
    {character}
  </span>
)

const BreadcrumbNav = ({ children, mobile = false }) => (
  <nav
    aria-label="breadcrumb"
    sx={{
      color: `text.secondary`,
      display: [
        `${mobile ? `inherit` : `none`}`,
        null,
        `${mobile ? `none` : `inherit`}`,
      ],
      fontSize: 1,
      mb: [7, null, 9],
    }}
  >
    {children}
  </nav>
)

const BreadcrumbLinkStyles = theme => ({
  color: theme.colors.grey[60],
  textDecoration: "none",
  "&:hover": {
    color: theme.colors.purple[50],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.purple[50],
    borderBottomStyle: "solid",
  },
})

const Breadcrumb = ({ location }) => {
  const itemList = getItemList(location.pathname)
  // provide escape if no itemList is provided so breadcrumb isn't rendered
  if (itemList === undefined) return null

  const activeItem = getActiveItem(itemList.items, location)
  const activeItemParents = getActiveItemParents(itemList.items, activeItem)
  const topLevel = itemList.key
  const topLevelTitle = itemList.breadcrumbTitle || itemList.title
  // return a shorter version of the breadcrumb on the intro page
  // because the docs intro page isn't generated from markdown
  if (activeItem.title === `Introduction`) {
    return (
      <React.Fragment>
        {/* only the breadcrumb nav of the proper viewport is displayed */}
        <BreadcrumbNav>
          <Link to="/">Home</Link>
          <Separator />
          {topLevelTitle}
        </BreadcrumbNav>
        <BreadcrumbNav mobile>
          <Separator character={<ChevronLeft />} />
          <Link to="/">Home</Link>
        </BreadcrumbNav>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      {/* render the default view on desktop sizes with all links displayed */}
      <BreadcrumbNav>
        <Link to="/">Home</Link>
        <Separator />
        <Link to={`/${topLevel}/`}>{topLevelTitle}</Link>
        <Separator />
        {activeItemParents.reverse().map(item => {
          const itemTitle = item.breadcrumbTitle || item.title
          return (
            <React.Fragment key={itemTitle}>
              <span>
                <Link to={item.link}>{itemTitle}</Link>
              </span>
              <Separator />
            </React.Fragment>
          )
        })}
        <span aria-current="page">
          {activeItem.breadcrumbTitle || activeItem.title}
        </span>
      </BreadcrumbNav>
      {/* render a smaller view on mobile viewports with only previous breadcrumb */}
      {activeItemParents && (
        <BreadcrumbNav mobile>
          <Separator character={<ChevronLeft />} />
          <Link
            to={
              activeItemParents[activeItemParents.length - 1]
                ? activeItemParents[activeItemParents.length - 1].link
                : `/${topLevel}/`
            }
          >
            {activeItemParents[activeItemParents.length - 1]
              ? activeItemParents[activeItemParents.length - 1]
                  .breadcrumbTitle ||
                activeItemParents[activeItemParents.length - 1].title
              : topLevelTitle}
          </Link>
        </BreadcrumbNav>
      )}
    </React.Fragment>
  )
}

export const findParents = (item, to) => {
  if (item.to === to) {
    return [item]
  }

  for (const subItem of item.subItems || []) {
    const parentTree = findParents(subItem, to)
    if (parentTree.length) {
      return [item, ...parentTree]
    }
  }

  return []
}

export const NewDocsBreadcrumb = ({ location }) => {
  const activeItemAndParents = findParents(allDocs[0], location.pathname) || []
  const getLink = item => item.to || (item.subItems && item.subItems[0].to)

  return (
    <React.Fragment>
      {/* render the default view on desktop sizes with all links displayed */}
      <BreadcrumbNav>
        {activeItemAndParents.map((item, i) => {
          const itemTitle = item.title || item.label
          return i < activeItemAndParents.length - 1 ? (
            <React.Fragment key={itemTitle}>
              <span>
                <Link
                  to={getLink(item)}
                  css={theme => BreadcrumbLinkStyles(theme)}
                >
                  {itemTitle}
                </Link>
              </span>
              <Separator
                character={
                  i === activeItemAndParents.length - 2 ? (
                    <ChevronRight />
                  ) : (
                    undefined
                  )
                }
              />
            </React.Fragment>
          ) : null
        })}
      </BreadcrumbNav>
      {/* render a smaller view on mobile viewports with only previous breadcrumb */}
      {activeItemAndParents.length > 1 && (
        <BreadcrumbNav mobile>
          <Separator character={<ChevronLeft />} />
          <Link to="/docs/" css={theme => BreadcrumbLinkStyles(theme)}>
            Docs
          </Link>
        </BreadcrumbNav>
      )}
    </React.Fragment>
  )
}

export default Breadcrumb
